/**
 * ConsentPage - Standalone consent screen
 *
 * This is an alternative entry point for consent that receives
 * pre-fetched context data, avoiding an extra API call.
 *
 * In most cases, AuthorizePage handles the full flow including
 * fetching context. This page is useful for:
 * - Direct navigation with consent data in state
 * - Retry after error
 */

import { useLocation, useNavigate } from 'react-router-dom'
import { AuthLayout } from '../components/layout/AuthLayout'
import {
  AppInfo,
  ConsentButtons,
  ConsentCard,
  ScopeList,
  UserInfo,
} from '../components/consent'

export function ConsentPage() {
  const location = useLocation()
  const navigate = useNavigate()

  // Get consent context from navigation state
  const context = location.state?.context

  // If no context, redirect to authorize
  if (!context) {
    // Try to reconstruct from URL params
    const searchParams = new URLSearchParams(location.search)
    const clientId = searchParams.get('client_id')
    const redirectUri = searchParams.get('redirect_uri')
    const scope = searchParams.get('scope')

    if (clientId && redirectUri && scope) {
      navigate(`/authorize${location.search}`, { replace: true })
    } else {
      navigate('/', { replace: true })
    }
    return null
  }

  const handleAllow = () => {
    // Redirect back to authorize with approval
    navigate(`/authorize${location.search}`, {
      replace: true,
      state: { approved: true },
    })
  }

  const handleDeny = () => {
    // Redirect with denial
    if (context.redirect_uri) {
      const url = new URL(context.redirect_uri)
      url.searchParams.set('error', 'access_denied')
      url.searchParams.set('error_description', 'User denied consent')
      if (context.state) url.searchParams.set('state', context.state)
      window.location.href = url.toString()
    } else {
      navigate('/', { replace: true })
    }
  }

  const handleSwitchAccount = () => {
    const returnTo = window.location.href
    navigate(`/login?return_to=${encodeURIComponent(returnTo)}`)
  }

  const isIncremental = context.new_scopes?.length < context.requested_scopes?.length

  return (
    <AuthLayout>
      <ConsentCard>
        {/* App Info */}
        <div className="mb-6">
          <AppInfo
            name={context.app.name}
            logoUrl={context.app.logo_url}
            homepageUrl={context.app.homepage_url}
          />
        </div>

        {/* Separator */}
        <div className="mb-6 border-t border-[var(--border-subtle)]" />

        {/* Message */}
        <p className="mb-4 text-sm text-[var(--text-secondary)]">
          {isIncremental
            ? `${context.app.name} is requesting additional access`
            : `${context.app.name} wants to access your account`}
        </p>

        {/* Scopes */}
        <div className="mb-6">
          <ScopeList
            scopes={context.requested_scopes}
            newScopes={context.new_scopes || []}
            title={isIncremental ? 'New permissions:' : 'This will allow the app to:'}
          />
        </div>

        {/* Separator */}
        <div className="mb-6 border-t border-[var(--border-subtle)]" />

        {/* User Info */}
        <div className="mb-6">
          <UserInfo
            email={context.user.email}
            displayName={context.user.display_name}
            onSwitchAccount={handleSwitchAccount}
          />
        </div>

        {/* Action Buttons */}
        <ConsentButtons onAllow={handleAllow} onDeny={handleDeny} />
      </ConsentCard>
    </AuthLayout>
  )
}
